import React, { useEffect, useState } from 'react';

const STORAGE_KEY   = 'aidey_pwa_prompt_dismissed';
const INSTALLED_KEY = 'aidey_pwa_installed';
const SHOW_DELAY_MS = 30000;

const NAVY   = '#0C2D48';
const YELLOW = '#E4E69B';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isMobile() {
  return window.innerWidth < 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

// Shared install-prompt event, also used by the Settings install section.
let _deferredPromptRef = null;
export function getInstallPrompt() { return _deferredPromptRef; }

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible]               = useState(false);
  const [ios, setIos]                       = useState(false);
  // Track the "already installed" banner separately so it can be dismissed
  const [showInstalledBanner, setShowInstalledBanner] = useState(false);
  const mobile = isMobile();

  // ── All hooks must run unconditionally ───────────────────────────────────────

  useEffect(() => {
    // Already running as the installed PWA — nothing to show.
    if (isStandalone()) return;

    // "Already installed" banner: was installed before, visiting in browser now.
    if (localStorage.getItem(INSTALLED_KEY) === 'true' &&
        localStorage.getItem(STORAGE_KEY) !== '1') {
      setShowInstalledBanner(true);
    }

    if (localStorage.getItem(STORAGE_KEY)) return;

    const onBeforeInstall = e => {
      e.preventDefault();
      _deferredPromptRef = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS: show share-to-homescreen instructions after delay (mobile only)
    let timer;
    if (isIOS() && mobile) {
      timer = setTimeout(() => { setIos(true); setVisible(true); }, SHOW_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      clearTimeout(timer);
    };
  }, [mobile]);

  // Show install banner once deferredPrompt is captured (desktop + Android Chrome)
  useEffect(() => {
    if (!deferredPrompt) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [deferredPrompt]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    _deferredPromptRef = null;
    if (outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, 'true');
    }
    dismiss();
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    setShowInstalledBanner(false);
  };

  // Mark as installed when iOS user taps Add to Home Screen (best-effort via appinstalled event)
  useEffect(() => {
    const onInstalled = () => localStorage.setItem(INSTALLED_KEY, 'true');
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────

  // Already running inside the installed PWA — show nothing.
  if (isStandalone()) return null;

  // "Aidey is installed on your device" — visiting the web version after install
  if (showInstalledBanner) {
    return (
      <div style={desktopStyles.banner}>
        <div style={desktopStyles.left}>
          <img
            src="https://d3fhl9qpik6455.cloudfront.net/brand/aidey-logo.jpg"
            alt="Aidey"
            style={desktopStyles.icon}
          />
          <div>
            <span style={desktopStyles.title}>Aidey is installed on your device</span>
            <span style={desktopStyles.body}>, open it for the best experience</span>
          </div>
        </div>
        <div style={desktopStyles.right}>
          <a
            href="https://app.aideyapp.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...desktopStyles.btnInstall, textDecoration: 'none', display: 'inline-block' }}
          >
            Open App
          </a>
          <button style={desktopStyles.btnDismiss} onClick={dismiss}>
            Not now
          </button>
          <button style={desktopStyles.btnClose} onClick={dismiss} aria-label="Dismiss">
            ×
          </button>
        </div>
      </div>
    );
  }

  if (!visible) return null;

  // ── Desktop banner — top, compact ─────────────────────────────────────────
  if (!mobile) {
    return (
      <div style={desktopStyles.banner}>
        <div style={desktopStyles.left}>
          <img
            src="https://d3fhl9qpik6455.cloudfront.net/brand/aidey-logo.jpg"
            alt="Aidey"
            style={desktopStyles.icon}
          />
          <div>
            <span style={desktopStyles.title}>Install Aidey as a desktop app</span>
            <span style={desktopStyles.body}>, for the best experience</span>
          </div>
        </div>
        <div style={desktopStyles.right}>
          <button style={desktopStyles.btnInstall} onClick={handleInstall}>
            Install
          </button>
          <button style={desktopStyles.btnDismiss} onClick={dismiss}>
            Not now
          </button>
          <button style={desktopStyles.btnClose} onClick={dismiss} aria-label="Dismiss">
            ×
          </button>
        </div>
      </div>
    );
  }

  // ── Mobile banner — bottom sheet ───────────────────────────────────────────
  return (
    <div style={mobileStyles.banner}>
      <div style={mobileStyles.content}>
        <img
          src="https://d3fhl9qpik6455.cloudfront.net/brand/aidey-logo.jpg"
          alt="Aidey"
          style={mobileStyles.icon}
        />
        <div style={mobileStyles.text}>
          {ios ? (
            <>
              <span style={mobileStyles.title}>Install Aidey</span>
              <span style={mobileStyles.body}>
                Tap <ShareIcon /> then <strong>"Add to Home Screen"</strong>
              </span>
            </>
          ) : (
            <>
              <span style={mobileStyles.title}>Add Aidey to your home screen</span>
              <span style={mobileStyles.body}>For the best experience</span>
            </>
          )}
        </div>
      </div>
      <div style={mobileStyles.actions}>
        {!ios && (
          <button
            style={mobileStyles.btnAdd}
            onClick={() => {
              localStorage.setItem(INSTALLED_KEY, 'true');
              handleInstall();
            }}
          >
            Add
          </button>
        )}
        {ios && (
          // For iOS we can't detect install programmatically, so set the flag
          // optimistically when the user taps "Add" in the share sheet flow.
          // The banner shows instructions; user must complete the flow themselves.
          <button
            style={mobileStyles.btnAdd}
            onClick={() => {
              localStorage.setItem(INSTALLED_KEY, 'true');
              dismiss();
            }}
          >
            Got it
          </button>
        )}
        <button style={mobileStyles.btnDismiss} onClick={dismiss}>Not now</button>
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24"
      fill="none" stroke="#f5c842" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: 'middle', margin: '0 2px' }}
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// ── Desktop styles ─────────────────────────────────────────────────────────────
const desktopStyles = {
  banner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 'var(--z-modal)',
    background: NAVY,
    borderBottom: `2px solid ${YELLOW}`,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    fontFamily: "'Raleway', sans-serif",
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  icon: {
    width: 28,
    height: 28,
    borderRadius: 7,
    flexShrink: 0,
  },
  title: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#fff',
  },
  body: {
    fontSize: '0.84rem',
    color: 'rgba(255,255,255,0.7)',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  btnInstall: {
    background: YELLOW,
    color: NAVY,
    border: 'none',
    borderRadius: 7,
    padding: '6px 18px',
    fontWeight: 800,
    fontSize: '0.83rem',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  btnDismiss: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.65)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: 7,
    padding: '6px 14px',
    fontSize: '0.82rem',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  btnClose: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
};

// ── Mobile styles ─────────────────────────────────────────────────────────────
const mobileStyles = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 'var(--z-modal)',
    background: '#13131f',
    borderTop: '1px solid rgba(245,200,66,0.2)',
    padding: '14px 16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#e8e8f0',
    fontFamily: 'Poppins, Inter, sans-serif',
  },
  body: {
    fontSize: '0.8rem',
    color: '#9090a8',
    fontFamily: 'Inter, sans-serif',
  },
  actions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  btnAdd: {
    background: '#f5c842',
    color: '#0a0a0f',
    border: 'none',
    borderRadius: 8,
    padding: '8px 20px',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontFamily: 'Poppins, Inter, sans-serif',
  },
  btnDismiss: {
    background: 'transparent',
    color: '#9090a8',
    border: '1px solid rgba(144,144,168,0.3)',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
  },
};
